import { facescan } from 'careplix-scan-sdk';
import type { ScanResults } from '@/types/health.types';
import type {
  CarePlixAddScanResponse,
  CareplyxError,
  FaceScanElements,
  FrameCallback,
} from '@/types/careplix.types';
import { mapCarePlixResponse } from '@/types/careplix.types';
import type { PostureValue } from '@/types/user.types';
import {
  createScanToken,
  submitScan,
  updateScanStatus,
  type AddScanInput,
} from './CareplixApi';
import { SCAN_FRAME_COLOR } from '@/utils/constants';
import { mapScanStartError, probeCameraAccess } from '@/utils/cameraPermission';

export interface ScanCallbacks {
  onCalibrating: () => void;
  onFaceNotDetected: () => void;
  onFaceDetected: () => void;
  onCountdown: (seconds: number) => void;
  onProgress: (percent: number) => void;
  onSignalStrength: (strength: 'weak' | 'moderate' | 'strong') => void;
  onAnalyzing: () => void;
  onComplete: (
    results: ScanResults,
    meta: { rawResponse: CarePlixAddScanResponse; scanToken: string },
  ) => void;
  onError: (error: CareplyxError) => void;
}

export interface ScanParams {
  apiKey: string;
  employeeId: string;
  scanToken: string;
  user: {
    heightCm: number;
    weightKg: number;
    gender: 'male' | 'female';
    dateOfBirth: string;
  };
  posture: PostureValue;
  elements: FaceScanElements;
}

import {
  getNetworkSignalStrength,
  subscribeToNetworkSignalChanges,
} from '@/utils/networkSignal';

function isFaceDetectionError(message: string): boolean {
  const lower = message.toLowerCase();
  return (
    lower.includes('cannot detect face') ||
    lower.includes('no suitable subject') ||
    lower.includes('no face')
  );
}

class CareplyxService {
  private apiKey = '';
  private employeeId = 'guest';
  private scanToken = '';
  private tokenExpiresAt = 0;
  /** CarePlix scan tokens are single-use — a retake must never reuse one. */
  private usedTokens = new Set<string>();
  private countdownTimer: ReturnType<typeof setInterval> | null = null;
  private unsubscribeNetwork: (() => void) | null = null;
  private sawCalibration = false;
  private sawScan = false;

  setCredentials(apiKey: string, employeeId?: string): void {
    this.apiKey = apiKey;
    if (employeeId) this.employeeId = employeeId;
  }

  async ensureScanToken(): Promise<string> {
    if (
      this.scanToken &&
      !this.usedTokens.has(this.scanToken) &&
      Date.now() < this.tokenExpiresAt
    ) {
      return this.scanToken;
    }
    const { token, expiresAt } = await createScanToken(this.apiKey, this.employeeId);
    this.scanToken = token;
    this.tokenExpiresAt = expiresAt;
    return token;
  }

  setScanToken(token: string, expiresAt: number): void {
    this.scanToken = token;
    this.tokenExpiresAt = expiresAt;
  }

  private clearCountdown(): void {
    if (this.countdownTimer) {
      clearInterval(this.countdownTimer);
      this.countdownTimer = null;
    }
  }

  private clearNetworkListener(): void {
    if (this.unsubscribeNetwork) {
      this.unsubscribeNetwork();
      this.unsubscribeNetwork = null;
    }
  }

  private reportSignalStrength(callbacks: ScanCallbacks): void {
    callbacks.onSignalStrength(getNetworkSignalStrength());
  }

  private startCountdown(callbacks: ScanCallbacks): void {
    this.clearCountdown();
    let seconds = 3;
    callbacks.onCountdown(seconds);
    this.countdownTimer = setInterval(() => {
      seconds -= 1;
      if (seconds > 0) {
        callbacks.onCountdown(seconds);
      } else {
        this.clearCountdown();
        callbacks.onProgress(0);
      }
    }, 1000);
  }

  async startScan(params: ScanParams, callbacks: ScanCallbacks): Promise<void> {
    this.sawCalibration = false;
    this.sawScan = false;
    this.clearCountdown();
    this.clearNetworkListener();

    this.reportSignalStrength(callbacks);
    this.unsubscribeNetwork = subscribeToNetworkSignalChanges(() => {
      this.reportSignalStrength(callbacks);
    });

    let scanToken = params.scanToken;
    if (!scanToken || this.usedTokens.has(scanToken) || Date.now() >= this.tokenExpiresAt) {
      scanToken = await this.ensureScanToken();
    }
    this.scanToken = scanToken;
    this.usedTokens.add(scanToken);

    const handleFrame: FrameCallback = (frame) => {
      this.reportSignalStrength(callbacks);

      if (frame.type === 'calibration') {
        this.sawCalibration = true;
        callbacks.onCalibrating();
        if (isFaceDetectionError(frame.message)) {
          callbacks.onFaceNotDetected();
        }
        return;
      }

      if (frame.type === 'error') {
        if (isFaceDetectionError(frame.message)) {
          callbacks.onFaceNotDetected();
        }
        return;
      }

      if (frame.type === 'scan') {
        if (!this.sawScan && this.sawCalibration) {
          this.sawScan = true;
          callbacks.onFaceDetected();
          this.startCountdown(callbacks);
        }
        if (!this.countdownTimer) {
          callbacks.onProgress(Math.round(frame.progress));
        }
      }
    };

    facescan.onFrame(handleFrame);

    facescan.onScanFinish((payload) => {
      void (async () => {
        this.clearCountdown();
        callbacks.onAnalyzing();
        try {
          const input: AddScanInput = {
            apiKey: params.apiKey,
            scanToken,
            posture: params.posture,
            dob: params.user.dateOfBirth,
            gender: params.user.gender,
            heightCm: params.user.heightCm,
            weightKg: params.user.weightKg,
            scanData: payload,
          };
          const response = await submitScan(input);
          const results = mapCarePlixResponse(response);
          callbacks.onComplete(results, { rawResponse: response, scanToken });
          void updateScanStatus(
            this.apiKey,
            this.employeeId,
            scanToken,
            'on_success',
            'facescan.onScanFinish',
          );
        } catch (err) {
          const message = err instanceof Error ? err.message : 'Failed to fetch scan results';
          callbacks.onError({ code: 'API_ERROR', message });
        }
      })();
    });

    facescan.onError((err, code, stackTrace) => {
      const lower = err.message.toLowerCase();
      if (
        lower.includes('permissions policy') ||
        lower.includes('not allowed in this document')
      ) {
        callbacks.onError({
          code: 'IFRAME_CAMERA_BLOCKED',
          message:
            'Camera is blocked in this embedded view. The host page must allow camera access on the iframe.',
        });
      } else if (err.message.includes('Camera')) {
        callbacks.onError({ code: 'CAMERA_PERMISSION_DENIED', message: err.message });
      } else {
        callbacks.onError({ code, message: err.message });
      }
      void updateScanStatus(this.apiKey, this.employeeId, scanToken, 'on_error', 'facescan.onError', [
        { code, stackTrace },
      ]);
    });

    const cameraAccess = await probeCameraAccess();
    if (!cameraAccess.ok) {
      callbacks.onError({ code: cameraAccess.code, message: cameraAccess.message });
      return;
    }

    try {
      await facescan.startScan({
        videoElement: params.elements.videoElement,
        canvasElement: params.elements.canvasElement,
        drawConfig: { type: 'face-circle', color: SCAN_FRAME_COLOR, lineSize: 4 },
        scanDuration: 60,
      });
    } catch (err) {
      const mapped = mapScanStartError(err);
      callbacks.onError(mapped);
    }
  }

  cancelScan(): void {
    this.clearCountdown();
    this.clearNetworkListener();
    facescan.stopScan();
    if (this.scanToken) {
      void updateScanStatus(this.apiKey, this.employeeId, this.scanToken, 'on_halt', 'cancel-scan');
    }
  }
}

export const careplyxService = new CareplyxService();
